<script setup>
import { downloadAttachment } from '../../api/messages';
import { bubbleTime as when, readTime } from '../../helpers/messageTime';
import { isJumbo } from '../../helpers/messageEmoji';
import MessageReactions from './MessageReactions.vue';

/**
 * The bubbles of one thread, for either end. Whoever is reading sees what they
 * wrote on the right and everyone else's on the left — tinted when it speaks
 * for the agency, plain when it is the policyholder's. Moments in the thread
 * (a broker taking over, a call) are centred.
 */
const props = defineProps({
  messages: { type: Array, default: () => [] },
  agencySide: { type: Boolean, default: false },
  otherName: { type: String, default: '' },
  agencyName: { type: String, default: 'Your agency' },
});

const emit = defineEmits(['join', 'react']);

const isSystem = (m) => m.kind === 'system' || m.kind === 'call';

const isMe = (m) => !isSystem(m) && !!m.mine;

const side = (m) => {
  if (isSystem(m)) return 'sys';

  return isMe(m) ? 'me' : ['them', { agency: !m.from_policyholder }];
};

const firstName = (name) => (name || '').trim().split(/\s+/)[0];

const person = (m) => {
  if (m.mine) return 'You';
  if (props.agencySide && !m.from_policyholder) return firstName(m.sender_name) || 'Agency';

  return props.agencySide ? firstName(m.sender_name) || props.otherName : m.sender_name || props.otherName;
};

const who = (m) => {
  if (!m.from_agency) return person(m);

  return props.agencySide ? `${person(m)} · sent as ${props.agencyName}` : props.agencyName;
};

const unassignedLine = (broker) => (props.agencySide
  ? `${broker} is no longer ${props.otherName}'s broker.`
  : `${broker} is no longer your broker. Anything you write here goes to ${props.agencyName} until a new broker is assigned.`);

const assignedLine = (broker, rawName) => (props.agencySide
  ? `${broker} is now ${props.otherName}'s broker.`
  : `${broker} is now your broker. Anything you write here goes to ${firstName(rawName) || 'them'}.`);

const handover = (m) => {
  const broker = m.broker_name || 'A broker';
  if (m.unassigned) return unassignedLine(broker);

  return assignedLine(broker, m.broker_name);
};

const sendingLabel = (m) => (typeof m.uploadProgress === 'number' ? `Uploading… ${m.uploadProgress}%` : 'Sending…');

// On your own messages only, at either end: whether the other end has read them.
const receipt = (m) => {
  if (!isMe(m)) return '';

  return m.read_at ? ` · Read ${when(m.read_at)}` : ' · Delivered';
};

const callLabel = (call) => {
  const kind = call?.mode === 'voice' ? 'Voice call' : 'Video call';
  if (!call?.ended_at) return kind;

  const minutes = Math.round((call.duration_seconds || 0) / 60);

  return minutes ? `${kind} ended · ${minutes} min` : `${kind} ended`;
};

const size = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
</script>

<template>
  <div class="msgs" data-testid="thread-messages">
    <slot name="before" />
    <div
      v-for="m in props.messages"
      :key="m.id"
      class="msgs__line"
      :class="{ 'msgs__line--me': isMe(m), 'msgs__line--sys': isSystem(m) }"
      data-testid="message"
    >
      <div v-if="m.kind === 'system'" class="bub sys" data-testid="handover">✓ {{ handover(m) }}</div>
      <div v-else-if="m.kind === 'call'" class="bub bub--call" :class="side(m)" data-testid="call-message">
        <span data-testid="call-label">{{ callLabel(m.call) }}</span>
        <button v-if="!m.call?.ended_at" type="button" class="bub__join" data-testid="join-call" @click="emit('join', { id: m.id, ...m.call })">Join the call</button>
      </div>
      <div v-else-if="m.body" class="bub" :class="[side(m), { jumbo: isJumbo(m.body) }]">{{ m.body }}</div>

      <div v-if="m.attachments?.length" class="bub bub--files" :class="side(m)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.3 3.3 0 0 1 4.7 4.7L10.2 17.7a1.7 1.7 0 0 1-2.4-2.4L15.5 7.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <template v-for="(file, index) in m.attachments" :key="file.id || file.name">
          <span v-if="index" aria-hidden="true"> · </span>
          <span v-if="m.sending" class="bub__file bub__file--pending">{{ file.name }}</span>
          <button
            v-else
            type="button"
            class="bub__file"
            data-testid="attachment"
            :title="`Download ${file.name} (${size(file.size)})`"
            @click="downloadAttachment(file)"
          >{{ file.name }}</button>
        </template>
      </div>

      <template v-if="m.kind !== 'system'">
        <MessageReactions v-if="m.kind !== 'call' && !m.sending" :reactions="m.reactions || []" :mine="isMe(m)" @toggle="(emoji) => emit('react', { message: m, emoji })" />
        <div class="mmeta" data-testid="message-meta"><template v-if="m.sending">You · <span data-testid="message-sending">{{ sendingLabel(m) }}</span></template><template v-else>{{ who(m) }} · {{ when(m.sent_at) }}<template v-if="agencySide || !m.read_at">{{ receipt(m) }}</template><span v-else-if="isMe(m)" class="mmeta__read" data-testid="read-mark"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.8 12.3 4.2 4.2L18.2 7.8" /></svg>Read {{ readTime(m.read_at) }}</span></template></div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.msgs {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

// The label sits right under its message; an empty reactions row (hover-only
// button) goes below the label so it never separates the two.
.msgs__line {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .bub + .bub { margin-top: 6px; }
  :deep(.rxrow.has) { order: 1; }
  .mmeta { order: 2; }
  :deep(.rxrow:not(.has)) { order: 3; margin-top: 0; }
}

.msgs__line--me {
  align-items: flex-end;

  .mmeta { text-align: right; }
}

.mmeta__read {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 5px;
  font-weight: 500;
  color: var(--xl-color-success);
}

.msgs__line--sys {
  align-items: center;

  .mmeta { text-align: center; }
}

.bub {
  box-sizing: border-box;
  width: fit-content;
  max-width: 76%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 14.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-align: left;
}

.bub.them {
  background: #fff;
  border: 1px solid var(--xl-color-border, #ede7f0);
  color: var(--xl-color-primary-text, #1c1720);
  border-bottom-left-radius: 4px;
}

.bub.them.agency {
  background: var(--xl-color-primary-container, #f5eff9);
  border-color: #d9c6e8;
  color: var(--xl-color-on-primary-container, #2a0e3c);
}

.bub.me {
  background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154a6), var(--xl-color-primary, #5e2f78));
  color: #fff;
  border-bottom-right-radius: 4px;
}

.bub.sys {
  max-width: 92%;
  padding: 6px 14px;
  border-radius: 14px;
  background: var(--xl-color-success-container, var(--xl-color-border));
  color: var(--xl-color-success, var(--xl-color-secondary-text));
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  text-align: center;
}

.bub.jumbo {
  padding: 2px 0;
  background: none;
  border: 0;
  box-shadow: none;
  font-size: 40px;
  line-height: 1.15;
  color: inherit;
}

.bub--files svg { display: inline-block; vertical-align: -2px; margin-right: 4px; }

.bub__file {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.bub--call {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bub__join {
  border: 0;
  border-radius: 99px;
  height: 30px;
  padding: 0 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #5e2f78;
}

.bub.sys.bub--call {
  background: var(--xl-color-border-subtle, #f3eff5);
  color: var(--xl-color-secondary-text, #514a57);
}

.bub.sys .bub__join {
  background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154a6), var(--xl-color-primary, #5e2f78));
  color: #fff;
}

.mmeta {
  font-size: 11.5px;
  color: var(--xl-color-outline-variant, #b6aebc);
  margin-top: 3px;
  align-self: stretch;
}
</style>
